import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Calendar, Bell, DollarSign, Upload, Image, Video, Trash2, Percent, Save, CreditCard, Palette } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { ThemeCustomizer } from './ThemeCustomizer';

interface SettingsSectionProps {
  getSetting: (key: string) => string;
  updateSetting: (key: string, value: string) => Promise<boolean>;
}

export const SettingsSection = ({ getSetting, updateSetting }: SettingsSectionProps) => {
  // Local state for form inputs
  const [deadlineInput, setDeadlineInput] = useState('');
  const [courseStartInput, setCourseStartInput] = useState('');
  const [groupPriceInput, setGroupPriceInput] = useState('');
  const [individualPriceInput, setIndividualPriceInput] = useState('');
  const [announcementTitleInput, setAnnouncementTitleInput] = useState('');
  const [announcementContentInput, setAnnouncementContentInput] = useState('');
  const [announcementActiveInput, setAnnouncementActiveInput] = useState(false);
  const [discountPercentageInput, setDiscountPercentageInput] = useState('');
  const [discountActiveInput, setDiscountActiveInput] = useState(false);
  const [discountEndDateInput, setDiscountEndDateInput] = useState('');
  const [discountDescriptionInput, setDiscountDescriptionInput] = useState('');
  
  // iDram settings state
  const [idramMerchantIdInput, setIdramMerchantIdInput] = useState('');
  const [idramSecretKeyInput, setIdramSecretKeyInput] = useState('');
  const [idramTestModeInput, setIdramTestModeInput] = useState(false);
  const [idramWebhookUrlInput, setIdramWebhookUrlInput] = useState('');
  
  // Loading states
  const [isUploading, setIsUploading] = useState(false);
  const [isUpdatingDeadline, setIsUpdatingDeadline] = useState(false);
  const [isUpdatingCourseStart, setIsUpdatingCourseStart] = useState(false);
  const [isUpdatingPrices, setIsUpdatingPrices] = useState(false);
  const [isUpdatingAnnouncement, setIsUpdatingAnnouncement] = useState(false);
  const [isUpdatingDiscount, setIsUpdatingDiscount] = useState(false);
  const [isUpdatingIdram, setIsUpdatingIdram] = useState(false);
  
  const { toast } = useToast();

  // Get current values from global context (for display)
  const currentDeadline = getSetting('admission_deadline') || '2025-06-30T22:00:00';
  const currentCourseStart = getSetting('course_start_date') || '2025-07-01T10:00:00';
  const currentGroupPrice = getSetting('group_price') || '60000';
  const currentIndividualPrice = getSetting('individual_price') || '90000';
  const currentAnnouncementTitle = getSetting('announcement_title') || '';
  const currentAnnouncementContent = getSetting('announcement_content') || '';
  const currentAnnouncementActive = getSetting('announcement_active') === 'true';
  const currentDiscountPercentage = getSetting('discount_percentage') || '0';
  const currentDiscountActive = getSetting('discount_active') === 'true';
  const currentDiscountEndDate = getSetting('discount_end_date') || '';
  const currentDiscountDescription = getSetting('discount_description') || '';
  const currentImageUrl = getSetting('announcement_image_url');
  const currentVideoUrl = getSetting('announcement_video_url');
  const currentIdramMerchantId = getSetting('idram_merchant_id') || '';
  const currentIdramSecretKey = getSetting('idram_secret_key') || '';
  const currentIdramTestMode = getSetting('idram_test_mode') === 'true';
  const currentIdramWebhookUrl = getSetting('idram_webhook_url') || '';

  const handleDeadlineUpdate = async () => {
    if (!deadlineInput.trim()) return;
    setIsUpdatingDeadline(true);
    try {
      const success = await updateSetting('admission_deadline', deadlineInput);
      if (success) {
        toast({
          title: "Թարմացվեց",
          description: "Ընդունելության ժամկետը թարմացվեց",
        });
        setDeadlineInput(''); // Clear input after successful update
      }
    } finally {
      setIsUpdatingDeadline(false);
    }
  };

  const handleCourseStartUpdate = async () => {
    if (!courseStartInput.trim()) return;
    setIsUpdatingCourseStart(true);
    try {
      const success = await updateSetting('course_start_date', courseStartInput);
      if (success) {
        toast({
          title: "Թարմացվեց",
          description: "Դասընթացի սկսման ամսաթիվը թարմացվեց",
        });
        setCourseStartInput(''); // Clear input after successful update
      }
    } finally {
      setIsUpdatingCourseStart(false);
    }
  };

  const handlePricesUpdate = async () => {
    if (!groupPriceInput.trim() && !individualPriceInput.trim()) return;
    setIsUpdatingPrices(true);
    try {
      const updates = [];
      if (groupPriceInput.trim()) {
        updates.push(updateSetting('group_price', groupPriceInput));
      }
      if (individualPriceInput.trim()) {
        updates.push(updateSetting('individual_price', individualPriceInput));
      }
      
      const results = await Promise.all(updates);
      if (results.every(result => result)) {
        toast({
          title: "Թարմացվեց",
          description: "Գները թարմացվեցին",
        });
        setGroupPriceInput(''); // Clear inputs after successful update
        setIndividualPriceInput('');
      }
    } finally {
      setIsUpdatingPrices(false);
    }
  };

  const handleAnnouncementSave = async () => {
    setIsUpdatingAnnouncement(true);
    try {
      const updates = [
        updateSetting('announcement_title', announcementTitleInput || currentAnnouncementTitle),
        updateSetting('announcement_content', announcementContentInput || currentAnnouncementContent),
        updateSetting('announcement_active', announcementActiveInput.toString())
      ];
      
      const results = await Promise.all(updates);
      if (results.every(result => result)) {
        toast({
          title: "Պահպանվեց",
          description: "Հայտարարությունը պահպանվեց",
        });
        setAnnouncementTitleInput('');
        setAnnouncementContentInput('');
        setAnnouncementActiveInput(false);
      }
    } finally {
      setIsUpdatingAnnouncement(false);
    }
  };

  const handleSaveDiscount = async () => {
    setIsUpdatingDiscount(true);
    try {
      const updates = [
        updateSetting('discount_percentage', discountPercentageInput || currentDiscountPercentage),
        updateSetting('discount_active', discountActiveInput.toString()),
        updateSetting('discount_end_date', discountEndDateInput || currentDiscountEndDate),
        updateSetting('discount_description', discountDescriptionInput || currentDiscountDescription)
      ];

      const results = await Promise.all(updates);
      if (results.every(result => result)) {
        toast({
          title: "Հաջողություն",
          description: "Զեղչի կարգավորումները պահպանվեցին",
        });
        setDiscountPercentageInput('');
        setDiscountActiveInput(false);
        setDiscountEndDateInput('');
        setDiscountDescriptionInput('');
      }
    } finally {
      setIsUpdatingDiscount(false);
    }
  };

  const handleSaveIdramSettings = async () => {
    setIsUpdatingIdram(true);
    try {
      const updates = [
        updateSetting('idram_merchant_id', idramMerchantIdInput || currentIdramMerchantId),
        updateSetting('idram_secret_key', idramSecretKeyInput || currentIdramSecretKey),
        updateSetting('idram_test_mode', idramTestModeInput.toString()),
        updateSetting('idram_webhook_url', idramWebhookUrlInput || currentIdramWebhookUrl)
      ];

      const results = await Promise.all(updates);
      if (results.every(result => result)) {
        toast({
          title: "Հաջողություն",
          description: "iDram-ի կարգավորումները պահպանվեցին",
        });
        setIdramMerchantIdInput('');
        setIdramSecretKeyInput('');
        setIdramTestModeInput(false);
        setIdramWebhookUrlInput('');
      }
    } finally {
      setIsUpdatingIdram(false);
    }
  };

  // Media functions
  const handleFileUpload = async (file: File, type: 'image' | 'video') => {
    try {
      setIsUploading(true);
      
      const fileExt = file.name.split('.').pop();
      const fileName = `announcement_${type}_${Date.now()}.${fileExt}`;
      
      const { data, error } = await supabase.storage
        .from('announcements')
        .upload(fileName, file);

      if (error) {
        console.error('Upload error:', error);
        throw error;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('announcements')
        .getPublicUrl(fileName);

      const settingKey = type === 'image' ? 'announcement_image_url' : 'announcement_video_url';
      const success = await updateSetting(settingKey, publicUrl);

      if (success) {
        toast({
          title: "Հաջողություն",
          description: `${type === 'image' ? 'Նկարը' : 'Վիդեոն'} հաջողությամբ բեռնվեց`,
        });
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast({
        title: "Սխալ",
        description: "Ֆայլը բեռնելիս տեղի ունեցավ սխալ",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveMedia = async (type: 'image' | 'video') => {
    const settingKey = type === 'image' ? 'announcement_image_url' : 'announcement_video_url';
    const success = await updateSetting(settingKey, '');
    
    if (success) {
      toast({
        title: "Հաջողություն",
        description: `${type === 'image' ? 'Նկարը' : 'Վիդեոն'} հեռացվեց`,
      });
    }
  };

  return (
    <div className="grid min-w-0 gap-4 lg:grid-cols-2 lg:gap-8">
      {/* Deadline Management */}
      <Card className="border-border/70 bg-card/80">
        <CardHeader>
          <CardTitle className="text-muted-foreground flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Ժամկետների կառավարում
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label className="text-muted-foreground">
              Ընդունելության ժամկետ
            </Label>
            <div className="text-sm text-muted-foreground mb-2">
              Ընթացիկ՝ {new Date(currentDeadline).toLocaleString('hy-AM')}
            </div>
            <Input
              type="datetime-local"
              value={deadlineInput}
              onChange={(e) => setDeadlineInput(e.target.value)}
              className="bg-muted border-border/70 text-foreground"
              placeholder={currentDeadline}
            />
            <Button 
              onClick={handleDeadlineUpdate}
              disabled={isUpdatingDeadline || !deadlineInput.trim()}
              className="w-full bg-primary hover:bg-primary/90"
            >
              {isUpdatingDeadline ? "Թարմացվում է..." : "Թարմացնել ժամկետը"}
            </Button>
          </div>

          <div className="space-y-2">
            <Label className="text-muted-foreground">
              Դասընթացի սկիզբ
            </Label>
            <div className="text-sm text-muted-foreground mb-2">
              Ընթացիկ՝ {new Date(currentCourseStart).toLocaleString('hy-AM')}
            </div>
            <Input
              type="datetime-local"
              value={courseStartInput}
              onChange={(e) => setCourseStartInput(e.target.value)}
              className="bg-muted border-border/70 text-foreground"
              placeholder={currentCourseStart}
            />
            <Button 
              onClick={handleCourseStartUpdate}
              disabled={isUpdatingCourseStart || !courseStartInput.trim()}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {isUpdatingCourseStart ? "Թարմացվում է..." : "Թարմացնել սկիզբը"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Pricing Management */}
      <Card className="border-border/70 bg-card/80">
        <CardHeader>
          <CardTitle className="text-muted-foreground flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Գների կառավարում
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-muted-foreground">
              Խմբային գին (ամսական)
            </Label>
            <div className="text-sm text-muted-foreground mb-2">
              Ընթացիկ՝ {currentGroupPrice} ԱՄԴ
            </div>
            <Input
              type="number"
              value={groupPriceInput}
              onChange={(e) => setGroupPriceInput(e.target.value)}
              className="bg-muted border-border/70 text-foreground"
              placeholder={currentGroupPrice}
            />
          </div>

          <div className="space-y-2">
            <Label className="text-muted-foreground">
              Անհատական գին (ամսական)
            </Label>
            <div className="text-sm text-muted-foreground mb-2">
              Ընթացիկ՝ {currentIndividualPrice} ԱՄԴ
            </div>
            <Input
              type="number"
              value={individualPriceInput}
              onChange={(e) => setIndividualPriceInput(e.target.value)}
              className="bg-muted border-border/70 text-foreground"
              placeholder={currentIndividualPrice}
            />
          </div>

          <Button 
            onClick={handlePricesUpdate}
            disabled={isUpdatingPrices || (!groupPriceInput.trim() && !individualPriceInput.trim())}
            className="w-full bg-purple-600 hover:bg-purple-700"
          >
            {isUpdatingPrices ? "Թարմացվում է..." : "Թարմացնել գները"}
          </Button>
        </CardContent>
      </Card>

      {/* Enhanced Announcement Management - spans full width */}
      <Card className="border-border/70 bg-card/80 lg:col-span-2">
        <CardHeader>
          <CardTitle className="text-muted-foreground flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Հայտարարությունների ամբողջական կառավարում
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="content" className="space-y-4">
            <div className="-mx-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <TabsList className="inline-flex h-auto min-w-max gap-1 border border-border/70 bg-muted/70 p-1.5 sm:grid sm:w-full sm:min-w-0 sm:grid-cols-5">
              <TabsTrigger value="content" className="min-h-11 shrink-0 px-3 text-xs text-muted-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground sm:px-2 sm:text-sm">
                <Bell className="h-4 w-4 mr-2" />
                Բովանդակություն
              </TabsTrigger>
              <TabsTrigger value="media" className="min-h-11 shrink-0 px-3 text-xs text-muted-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground sm:px-2 sm:text-sm">
                <Upload className="h-4 w-4 mr-2" />
                Մեդիա
              </TabsTrigger>
              <TabsTrigger value="discount" className="min-h-11 shrink-0 px-3 text-xs text-muted-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground sm:px-2 sm:text-sm">
                <Percent className="h-4 w-4 mr-2" />
                Զեղչեր
              </TabsTrigger>
              <TabsTrigger value="idram" className="min-h-11 shrink-0 px-3 text-xs text-muted-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground sm:px-2 sm:text-sm">
                <CreditCard className="h-4 w-4 mr-2" />
                iDram
              </TabsTrigger>
              <TabsTrigger value="theme" className="min-h-11 shrink-0 px-3 text-xs text-muted-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground sm:px-2 sm:text-sm">
                <Palette className="h-4 w-4 mr-2" />
                Թեմա
              </TabsTrigger>
            </TabsList>
            </div>

            <TabsContent value="content" className="space-y-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">
                    Վերնագիր
                  </Label>
                  <div className="text-sm text-muted-foreground mb-2">
                    Ընթացիկ՝ {currentAnnouncementTitle || 'Նշված չէ'}
                  </div>
                  <Input
                    value={announcementTitleInput}
                    onChange={(e) => setAnnouncementTitleInput(e.target.value)}
                    className="bg-muted border-border/70 text-foreground"
                    placeholder="Կարևոր հայտարարություն..."
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-muted-foreground">
                    Բովանդակություն
                  </Label>
                  <div className="text-sm text-muted-foreground mb-2">
                    Ընթացիկ՝ {currentAnnouncementContent || 'Նշված չէ'}
                  </div>
                  <Textarea
                    value={announcementContentInput}
                    onChange={(e) => setAnnouncementContentInput(e.target.value)}
                    className="bg-muted border-border/70 text-foreground min-h-[100px]"
                    placeholder="Հայտարարության բովանդակությունը..."
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border/70">
                  <div className="space-y-1">
                    <Label className="text-muted-foreground">Ակտիվ է</Label>
                    <p className="text-sm text-muted-foreground/70">
                      Ընթացիկ՝ {currentAnnouncementActive ? 'Այո' : 'Ոչ'}
                    </p>
                  </div>
                  <Switch
                    checked={announcementActiveInput}
                    onCheckedChange={setAnnouncementActiveInput}
                  />
                </div>
              </div>

              <Button 
                onClick={handleAnnouncementSave}
                disabled={isUpdatingAnnouncement}
                className="w-full bg-amber-600 hover:bg-amber-700"
              >
                {isUpdatingAnnouncement ? "Պահպանվում է..." : "Պահպանել հայտարարությունը"}
              </Button>
            </TabsContent>

            <TabsContent value="media" className="space-y-6">
              {/* Image Upload */}
              <div className="space-y-3">
                <Label className="text-muted-foreground flex items-center gap-2">
                  <Image className="h-4 w-4" />
                  Նկար
                </Label>
                {currentImageUrl ? (
                  <div className="space-y-2">
                    <img 
                      src={currentImageUrl} 
                      alt="Announcement" 
                      className="h-auto w-full max-w-xs rounded-lg border border-border/70"
                    />
                    <Button
                      onClick={() => handleRemoveMedia('image')}
                      variant="outline"
                      size="sm"
                      className="text-red-400 border-red-400/30 hover:bg-red-400/20"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Հեռացնել նկարը
                    </Button>
                  </div>
                ) : (
                  <div>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileUpload(file, 'image');
                      }}
                      disabled={isUploading}
                      className="bg-muted border-border/70 text-foreground"
                    />
                  </div>
                )}
              </div>

              {/* Video Upload */}
              <div className="space-y-3">
                <Label className="text-muted-foreground flex items-center gap-2">
                  <Video className="h-4 w-4" />
                  Վիդեո
                </Label>
                {currentVideoUrl ? (
                  <div className="space-y-2">
                    <video 
                      src={currentVideoUrl} 
                      controls 
                      className="h-auto w-full max-w-xs rounded-lg border border-border/70"
                    />
                    <Button
                      onClick={() => handleRemoveMedia('video')}
                      variant="outline"
                      size="sm"
                      className="text-red-400 border-red-400/30 hover:bg-red-400/20"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Հեռացնել վիդեոն
                    </Button>
                  </div>
                ) : (
                  <div>
                    <Input
                      type="file"
                      accept="video/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileUpload(file, 'video');
                      }}
                      disabled={isUploading}
                      className="bg-muted border-border/70 text-foreground"
                    />
                  </div>
                )}
              </div>

              {isUploading && (
                <div className="text-center py-4">
                  <div className="text-muted-foreground">Բեռնվում է...</div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="discount" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Զեղչի տոկոս (%)</Label>
                  <div className="text-sm text-muted-foreground mb-2">
                    Ընթացիկ՝ {currentDiscountPercentage}%
                  </div>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={discountPercentageInput}
                    onChange={(e) => setDiscountPercentageInput(e.target.value)}
                    className="bg-muted border-border/70 text-foreground"
                    placeholder={currentDiscountPercentage}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-muted-foreground flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Ավարտի ամսաթիվ
                  </Label>
                  <div className="text-sm text-muted-foreground mb-2">
                    Ընթացիկ՝ {currentDiscountEndDate ? new Date(currentDiscountEndDate).toLocaleString('hy-AM') : 'Նշված չէ'}
                  </div>
                  <Input
                    type="datetime-local"
                    value={discountEndDateInput}
                    onChange={(e) => setDiscountEndDateInput(e.target.value)}
                    className="bg-muted border-border/70 text-foreground"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground">Զեղչի նկարագրություն</Label>
                <div className="text-sm text-muted-foreground mb-2">
                  Ընթացիկ՝ {currentDiscountDescription || 'Նշված չէ'}
                </div>
                <Textarea
                  value={discountDescriptionInput}
                  onChange={(e) => setDiscountDescriptionInput(e.target.value)}
                  className="bg-muted border-border/70 text-foreground"
                  placeholder="Զեղչի մանրամասները..."
                  rows={3}
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border/70">
                <div className="space-y-1">
                  <Label className="text-muted-foreground">Զեղչը ակտիվ է</Label>
                  <p className="text-sm text-muted-foreground/70">
                    Ընթացիկ՝ {currentDiscountActive ? 'Այո' : 'Ոչ'}
                  </p>
                </div>
                <Switch
                  checked={discountActiveInput}
                  onCheckedChange={setDiscountActiveInput}
                />
              </div>

              <Button
                onClick={handleSaveDiscount}
                disabled={isUpdatingDiscount}
                className="w-full bg-primary hover:bg-primary/90"
              >
                <Save className="h-4 w-4 mr-2" />
                {isUpdatingDiscount ? "Պահպանվում է..." : "Պահպանել զեղչի կարգավորումները"}
              </Button>

              {currentDiscountActive && parseInt(currentDiscountPercentage) > 0 && (
                <div className="p-4 bg-green-500/10 rounded-lg border border-border/70 text-center">
                  <p className="text-muted-foreground text-lg font-bold">
                    Ընթացիկ զեղչ՝ {currentDiscountPercentage}%
                  </p>
                  {currentDiscountDescription && (
                    <p className="text-muted-foreground text-sm mt-2">
                      {currentDiscountDescription}
                    </p>
                  )}
                  {currentDiscountEndDate && (
                    <p className="text-muted-foreground text-xs mt-1">
                      Վավեր է մինչև՝ {new Date(currentDiscountEndDate).toLocaleString('hy-AM')}
                    </p>
                  )}
                </div>
              )}
            </TabsContent>

            <TabsContent value="idram" className="space-y-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Merchant ID</Label>
                  <div className="text-sm text-muted-foreground mb-2">
                    Ընթացիկ՝ {currentIdramMerchantId || 'Նշված չէ'}
                  </div>
                  <Input
                    value={idramMerchantIdInput}
                    onChange={(e) => setIdramMerchantIdInput(e.target.value)}
                    className="bg-muted border-border/70 text-foreground"
                    placeholder="iDram-ի Merchant ID-ն"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-muted-foreground">Secret Key</Label>
                  <div className="text-sm text-muted-foreground mb-2">
                    Ընթացիկ՝ {currentIdramSecretKey ? '*'.repeat(currentIdramSecretKey.length) : 'Նշված չէ'}
                  </div>
                  <Input
                    type="password"
                    value={idramSecretKeyInput}
                    onChange={(e) => setIdramSecretKeyInput(e.target.value)}
                    className="bg-muted border-border/70 text-foreground"
                    placeholder="iDram-ի գաղտնի բանալին"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-muted-foreground">Webhook URL</Label>
                  <div className="text-sm text-muted-foreground mb-2">
                    Ընթացիկ՝ {currentIdramWebhookUrl || 'Նշված չէ'}
                  </div>
                  <Input
                    value={idramWebhookUrlInput}
                    onChange={(e) => setIdramWebhookUrlInput(e.target.value)}
                    className="bg-muted border-border/70 text-foreground"
                    placeholder="https://example.com/webhook/idram"
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border/70">
                  <div className="space-y-1">
                    <Label className="text-muted-foreground">Թեստային ռեժիմ</Label>
                    <p className="text-sm text-muted-foreground/70">
                      Ընթացիկ՝ {currentIdramTestMode ? 'Այո' : 'Ոչ'}
                    </p>
                  </div>
                  <Switch
                    checked={idramTestModeInput}
                    onCheckedChange={setIdramTestModeInput}
                  />
                </div>

                <Button
                  onClick={handleSaveIdramSettings}
                  disabled={isUpdatingIdram}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  {isUpdatingIdram ? "Պահպանվում է..." : "Պահպանել iDram կարգավորումները"}
                </Button>

                <div className="rounded-lg border border-border/70 bg-primary/10 p-4">
                  <h4 className="mb-2 font-bold text-primary">iDram ինտեգրում</h4>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    <li>• Merchant ID-ն և Secret Key-ը ստանալու համար գրանցվեք iDram-ում</li>
                    <li>• Webhook URL-ն պետք է հասանելի լինի հանրային ցանցից</li>
                    <li>• Թեստային ռեժիմը թույլ է տալիս փորձարկել առանց իրական գումար վճարելու</li>
                  </ul>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="theme" className="space-y-4">
              <ThemeCustomizer 
                getSetting={getSetting}
                updateSetting={updateSetting}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};